/**
 * Authentication state. Light client state only — server data belongs to TanStack Query.
 */
import { create } from "zustand";

import { queryClient } from "@/app/queryClient";
import {
  clearActiveAgency,
  decideAgency,
  readActiveAgency,
  writeActiveAgency,
  type AgencyChoice,
} from "@/lib/activeAgency";
import { ApiError, apiRequest, refreshAccessToken } from "@/lib/api";
import { clearAccessToken, purgeLegacyTokenStorage, writeAccessToken } from "@/lib/tokens";

export type Role = "operator" | "branch_manager" | "moliyachi" | "owner";

export interface AuthBranch {
  id: number;
  name: string;
}

export interface AuthTenant {
  id: number;
  name: string;
  logo_url: string | null;
  /** The agency's lifecycle state. `suspended_admin` replaces the pages with a screen. */
  status: "active" | "suspended_billing" | "suspended_admin" | "closed";
  operator_sees_margin: boolean;
}

export interface AuthUser {
  id: number;
  email: string;
  full_name: string;
  /** Kapitan staff, above any agency. Gates the platform panel and the ui-kit page. */
  is_platform_admin: boolean;
  /** Empty for a platform admin who belongs to no agency. */
  memberships: readonly unknown[];
  /** Null only for a platform admin with no agency membership. */
  role: Role | null;
  branch: AuthBranch | null;
  /** Null only for a platform admin with no agency membership. */
  tenant: AuthTenant | null;
  /** §5 Profil. Absent for a platform admin with no agency membership. */
  phone?: string;
  display_name?: string;
  hire_date?: string | null;
  avatar_url?: string;
  telegram_username?: string;
  two_factor_enabled?: boolean;
}

/**
 * What a password sign-in produced. With two-factor on (D9.9) the password step hands over
 * nothing: the login page waits for the person's own Telegram to approve the challenge.
 */
export type LoginOutcome =
  | { kind: "signed-in" }
  | { kind: "two-factor"; challenge: string; expiresIn: number };

/** One poll of a two-factor challenge. */
export type ChallengeOutcome = "signed-in" | "pending" | "denied" | "expired";

interface TwoFactorPending {
  two_factor: true;
  challenge: string;
  expires_in: number;
}

interface TokenResponse {
  /** The refresh token is an HttpOnly cookie, never a body value. */
  access: string;
}

/** `GET /api/auth/tenants/`: every agency the signed-in person may work in. */
interface TenantChoicesResponse {
  tenants: AgencyChoice[];
}

/** Where the session stands once the person is known. */
type SessionState = Pick<AuthState, "user" | "status" | "agencies">;

interface AuthState {
  user: AuthUser | null;
  /**
   * `choose-agency`: the person belongs to several agencies and has not picked one. They see
   * the chooser, not the product, because every request would otherwise be refused (TZ §17.2).
   * `unreachable`: the API could not be asked at all (no connection, a gateway error while it
   * restarts). Says nothing about the session, so the person is offered a retry rather than
   * the login page, and is never left on the loader.
   */
  status: "unknown" | "authenticated" | "anonymous" | "choose-agency" | "unreachable";
  /** The agencies the person belongs to, for the switcher. Empty when there is only none. */
  agencies: readonly AgencyChoice[];
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /** Polls a two-factor challenge; on approval signs the person in exactly as `login` does. */
  pollChallenge: (challenge: string) => Promise<ChallengeOutcome>;
  /** Re-reads `/me` after the person changed their own profile. */
  reloadUser: () => Promise<void>;
  logout: () => void;
  restore: () => Promise<void>;
  /** Makes `id` the working agency and reloads, so no screen keeps the previous one's data. */
  switchAgency: (id: number) => void;
}

/**
 * Works out which agency the session is in, then reads the person. The choice is made before
 * `/me` is asked: `/me` is refused with several memberships and no agency named.
 */
async function establishSession(): Promise<SessionState> {
  const choices = await apiRequest<TenantChoicesResponse>("/api/auth/tenants/");
  const agencies = choices.tenants;
  const decision = decideAgency(agencies, readActiveAgency());

  if (decision.kind === "choose") {
    return { user: null, status: "choose-agency", agencies };
  }
  if (decision.kind === "use") {
    writeActiveAgency(decision.id);
  } else {
    clearActiveAgency();
  }
  const user = await apiRequest<AuthUser>("/api/auth/me/");
  return { user, status: "authenticated", agencies };
}

/**
 * Whether a failed session call says the session is over, or only that the server did not
 * answer. A refusal (4xx) ends it; a dropped connection or a 5xx while a deploy restarts the
 * API is the server being away, and reading that as a logout would send a signed-in person
 * to the login form.
 */
export function endsSession(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: "unknown",
  agencies: [],

  login: async (email: string, password: string): Promise<LoginOutcome> => {
    const answer = await apiRequest<TokenResponse | TwoFactorPending>("/api/auth/token/", {
      method: "POST",
      body: { email, password },
      anonymous: true,
    });
    if ("two_factor" in answer) {
      return { kind: "two-factor", challenge: answer.challenge, expiresIn: answer.expires_in };
    }
    // Drop every cached response before the new identity can read one. Queries are
    // keyed by what they fetch, not by who fetched it, so without this the next user
    // is served the previous user's rows from cache until they go stale -- on a
    // shared office machine that is one person seeing another's pipeline.
    queryClient.clear();
    writeAccessToken(answer.access);
    set(await establishSession());
    return { kind: "signed-in" };
  },

  pollChallenge: async (challenge: string): Promise<ChallengeOutcome> => {
    let answer: TokenResponse | { status: string };
    try {
      answer = await apiRequest<TokenResponse | { status: string }>("/api/auth/2fa/challenge/", {
        method: "POST",
        body: { challenge },
        anonymous: true,
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) return "denied";
      if (error instanceof ApiError && error.status === 410) return "expired";
      throw error;
    }
    if (!("access" in answer)) return "pending";
    queryClient.clear();
    writeAccessToken(answer.access);
    set(await establishSession());
    return "signed-in";
  },

  reloadUser: async (): Promise<void> => {
    const user = await apiRequest<AuthUser>("/api/auth/me/");
    set({ user });
  },

  logout: (): void => {
    // Ask the server to blacklist the refresh token and clear the cookie. Fire and
    // forget: the local session ends either way, and a failed network call must not
    // leave someone apparently still signed in on a shared machine.
    void apiRequest("/api/auth/token/logout/", { method: "POST", anonymous: true }).catch(
      () => undefined,
    );
    clearAccessToken();
    clearActiveAgency();
    queryClient.clear();
    set({ user: null, status: "anonymous", agencies: [] });
  },

  restore: async (): Promise<void> => {
    // Anything a previous version left in localStorage is a credential we no longer
    // use; clear it rather than leave it lying about.
    purgeLegacyTokenStorage();
    set({ status: "unknown" });

    // The access token died with the tab, so the only way back in is the refresh
    // cookie. If the browser has none, or it has been blacklisted, this is simply a
    // signed-out visitor. A fetch that rejects never reached the server: that is the
    // unreachable state, not a logout, and never a reason to stay on the loader.
    let refreshed: boolean;
    try {
      refreshed = await refreshAccessToken();
    } catch {
      set({ user: null, status: "unreachable", agencies: [] });
      return;
    }
    if (!refreshed) {
      set({ user: null, status: "anonymous", agencies: [] });
      return;
    }
    try {
      set(await establishSession());
    } catch (error) {
      if (!endsSession(error)) {
        set({ user: null, status: "unreachable", agencies: [] });
        return;
      }
      // An expired or revoked token, or a membership that was deactivated: signed out.
      clearAccessToken();
      set({ user: null, status: "anonymous", agencies: [] });
    }
  },

  switchAgency: (id: number): void => {
    if (!get().agencies.some((agency) => agency.id === id)) {
      // Not one of the person's agencies: nothing to switch to, and the server would refuse it.
      return;
    }
    writeActiveAgency(id);
    // A full load, not a cache swap: the realtime socket and every open query belong to the
    // agency being left, and a reload is the one way to be sure none of them survives.
    window.location.assign("/");
  },
}));
